import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { Button, cn } from '@asad/crane-ui'

import { alertPresentationOf, workingPresentationOf } from '../config/statusConfig'
import { DeviceStatusType, MapDevice } from '../types'

export interface DeviceListPanelProps {
  devices: MapDevice[]
  devicesStatuses: DeviceStatusType[]
  selectedDeviceId?: string
  onSelect?: (deviceId: string) => void
  /** Przejście do szczegółów urządzenia — ikona po prawej stronie pozycji. */
  onOpenDetails?: (deviceId: string) => void
  /** Backend nie ma encji lokalizacji — nazwa wchodzi propsem, gdy widok potrafi ją wyznaczyć. */
  locationLabel?: (device: MapDevice) => string
  /** Stały nagłówek listy (tytuł + licznik). Zostaje widoczny przy przewijaniu. */
  title?: string
  /** Panel wypełnia wysokość rodzica i przewija się WEWNĄTRZ karty (zakładka Mapa).
   *  Domyślnie `false` — sticky + `max-h` liczone od okna, przewija się CAŁA karta. */
  fillHeight?: boolean
  /** Panel przykleja się do okna przy przewijaniu STRONY (zakładka Urządzenia):
   *  sticky + `max-h` ekranu, ale nagłówek (tytuł + licznik) zostaje na miejscu,
   *  a przewija się WYŁĄCZNIE lista pozycji. Nie łączyć z `fillHeight`. */
  stickyViewport?: boolean
  className?: string
}

/** Lista urządzeń obok mapy / siatki kart.
 *  Filtrowanie i wyszukiwanie należą do widoku — panel dostaje gotową listę `devices`.
 *  Każda pozycja pokazuje obie osie statusu: ikonę `isWorking` + kropkę `alertStatus`,
 *  a po prawej ikonę przejścia do szczegółów urządzenia. */
export const DeviceListPanel = ({
  devices,
  devicesStatuses,
  selectedDeviceId,
  onSelect,
  onOpenDetails,
  locationLabel,
  title,
  fillHeight = false,
  stickyViewport = false,
  className,
}: DeviceListPanelProps) => {
  const { t } = useTranslation()
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const statusById = useMemo(() => new Map(devicesStatuses.map((status) => [status.id, status])), [devicesStatuses])

  useEffect(() => {
    if (!selectedDeviceId) return
    rowRefs.current[selectedDeviceId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selectedDeviceId])

  return (
    <aside
      className={cn(
        'flex w-full shrink-0 flex-col rounded-crane-md bg-crane-card p-3 shadow-crane-soft md:w-crane-panel',
        fillHeight
          ? 'h-full min-h-0'
          : stickyViewport
          ? 'min-h-0 self-start md:sticky md:top-crane-sticky md:max-h-crane-sticky-panel'
          : 'self-start overflow-y-auto md:sticky md:top-crane-sticky md:max-h-crane-sticky-panel',
        className,
      )}
    >
      {title ? (
        <div className='mb-2 flex shrink-0 items-center justify-between gap-2 px-1.5'>
          <span className='truncate text-xs font-semibold uppercase tracking-crane-label text-crane-muted-foreground'>
            {title}
          </span>
          <span className='shrink-0 text-crane-xs text-crane-muted-foreground'>{devices.length}</span>
        </div>
      ) : null}
      <div
        className={cn(
          'flex flex-col gap-crane-hairline',
          (fillHeight || stickyViewport) && 'min-h-0 flex-1 overflow-y-auto',
        )}
      >
        {devices.map((device) => {
          const status = statusById.get(device.id)
          const working = workingPresentationOf(status)
          const alert = alertPresentationOf(status)
          const WorkingIcon = working.Icon
          const selected = selectedDeviceId === device.id
          const location = locationLabel?.(device)

          return (
            <div
              key={device.id}
              ref={(el) => {
                rowRefs.current[device.id] = el
              }}
              className={cn(
                'group flex cursor-pointer items-center gap-2.5 rounded-crane-lg py-1.5 pl-2.5 pr-2 text-crane-xl text-crane-foreground outline-none transition-colors',
                'hover:bg-crane-accent focus-visible:ring-1 focus-visible:ring-crane-ring',
                selected && 'bg-crane-accent font-bold ring-1 ring-inset ring-crane-border',
              )}
              onClick={() => onSelect?.(device.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect?.(device.id)
                }
              }}
              role='button'
              tabIndex={0}
              aria-pressed={selected}
              title={`${device.name} · ${t(working.labelKey)} · ${t(alert.labelKey)}`}
            >
              <WorkingIcon className={cn('size-3 shrink-0', working.text)} aria-hidden />
              <span className={cn('size-2 shrink-0 rounded-crane-sm', alert.dot)} aria-hidden />
              <span className='flex min-w-0 flex-1 flex-col'>
                <span className='truncate text-xs'>{device.name}</span>
                {location ? (
                  <span className='truncate text-crane-2xs font-normal text-crane-muted-foreground'>{location}</span>
                ) : null}
              </span>

              {onOpenDetails ? (
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  aria-label={t('device.openDetails')}
                  title={t('device.openDetails')}
                  className='size-6 shrink-0 rounded-crane-md text-crane-muted-foreground hover:bg-crane-card-elevated hover:text-crane-foreground'
                  onClick={(event) => {
                    event.stopPropagation()
                    onOpenDetails(device.id)
                  }}
                >
                  <ChevronRight className='size-4' aria-hidden />
                </Button>
              ) : null}
            </div>
          )
        })}

        {devices.length === 0 && (
          <span className='p-1 text-xs text-crane-muted-foreground'>{t('common.noResults')}</span>
        )}
      </div>
    </aside>
  )
}
